<!-- The form that signs a learner in. Shared by the plain sign-in view and the
     Via introduction, which is the same dialog with a different head: two
     copies of it drifted apart once already. -->
<ng-template #magicLinkForm>
  <form [formGroup]="emailFormGroup" (ngSubmit)="onEmailFormSubmit()">
    <!-- A send that failed used to leave this card looking exactly like a send
         that had not been made yet. -->
    @if (magicLinkError()) {
      <p class="ac-err" role="alert">{{ magicLinkError() }}</p>
    }

    <label class="ac-field">
      <span>Email</span>
      <span class="ac-input">
        <!-- Drawn, not from an icon pack: a missing registration throws while
             the field updates and takes the rest of its bindings with it. -->
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
             stroke-linejoin="round" aria-hidden="true">
          <rect x="2.8" y="5.2" width="18.4" height="13.6" rx="2" />
          <path d="M3.4 6.4 12 13l8.6-6.6" />
        </svg>
        <input
          type="email"
          formControlName="email"
          autocomplete="email"
          placeholder="your@email.com"
          [attr.aria-invalid]="
            emailFormGroup.controls.email.touched &&
            emailFormGroup.controls.email.invalid
          "
        />
      </span>

      <!-- Only once they have left the field: telling somebody their address
           is invalid while they are still typing it is not help. -->
      @if (
        emailFormGroup.controls.email.touched &&
        emailFormGroup.controls.email.invalid
      ) {
        <em class="ac-msg" role="alert">
          {{
            emailFormGroup.controls.email.errors?.required
              ? 'Enter your email address'
              : 'Enter a valid email address, like name@example.com'
          }}
        </em>
      }
    </label>

    <div class="actions">
      <cpd-flat-button
        type="submit"
        color="primary"
        size="large"
        text="Sign in with Magic Link"
      >
      </cpd-flat-button>

      <p class="ac-note">
        We will send a link that signs you in. It expires in <b>15 minutes</b>
        and can only be used once, so there is no password to remember or lose.
      </p>

      <div class="divider">
        <hr />
        <p>or</p>
        <hr />
      </div>

      <div class="social-login">
        <button type="button" class="btn-google" (click)="signinWithGoogle()">
          <mat-icon svgIcon="google"></mat-icon>
          Sign in with Google
        </button>
        <button
          type="button"
          class="btn-linkedin"
          (click)="signinWithLinkedIn()"
        >
          <mat-icon svgIcon="linkedin"></mat-icon>
          Sign in with LinkedIn
        </button>
      </div>
    </div>
  </form>
</ng-template>

@if (isViaIntroOpen() && view() === 'signin') {
  <!-- Via introduction: the same dialog, opened when Via prompts for an
       account, explaining what an account is for before asking for one. -->
  <section>
    <section class="dialog-header">
      <button
        type="button"
        class="link-button close-icon"
        aria-label="Close"
        (click)="close()"
      >
        <mat-icon svgIcon="close-white"></mat-icon>
      </button>
      <mat-icon class="arrow-right" svgIcon="arrow-right"></mat-icon>
      <div class="dialog-title">
        <h1 #viewHeading tabindex="-1">Introducing Via</h1>
        <p>Your virtual intelligence advisor.</p>
      </div>
      <div class="img-container">
        <img alt="" src="assets/images/chat-gpt/app-screenshot.png" />
      </div>
    </section>
    <section class="dialog-body">
      <div class="guide-text">
        <p>
          Meet Via, your personal virtual intelligence advisor that helps you
          explore career and education options, compare opportunities, and make
          confident decisions about your future.
        </p>
        <p>
          <b>Sign in or create an account today for free</b> to chat with Via
          and save your progress.
        </p>
      </div>
      <ng-container *ngTemplateOutlet="magicLinkForm"></ng-container>
      <p class="ac-band">
        Counselor or administrator?
        <button class="ac-link" type="button" (click)="go('counselor')">
          Sign in here
        </button>
      </p>
    </section>
  </section>
} @else {
  <cpd-dialog-container2 [size]="'645px'">
    <div dialog-header class="ac-head">
      <h1 #viewHeading tabindex="-1">{{ heading() }}</h1>

      @switch (view()) {
        @case ('signin') {
          <p>
            A Career Path DECIDE account is free and saves your progress, so you
            pick up where you left off. Just enter your email.
          </p>
        }
        @case ('sent') {
          <p>
            A magic link is on its way to <b>{{ sentToAddress() }}</b
            >. Click the link in your email to finish signing in.
          </p>
        }
        @case ('reg1') {
          <p>Two steps. This one is who you are.</p>
        }
        @case ('reg2') {
          <p>
            This is how we translate your job code, find the transition office
            nearest you, and price occupations where you are.
          </p>
        }
        @case ('counselor') {
          <p>
            Same product, different permissions. Counselor and administrator
            accounts are opened for you, so this one takes a password.
          </p>
        }
        @case ('continue') {
          <p>
            You are signing in as <b>{{ landedEmail() }}</b>.
          </p>
        }
        @case ('used') {
          <p>
            Each sign-in link works once and expires after 15 minutes. This one
            has already been used or has expired.
          </p>
        }
        <!-- AUTH.9. Copy approved by Jackson, 13 Oct 2026. -->
        @case ('returnExpired') {
          <p>
            Links to a Goals Report stop working after a while to keep your
            information safe. Your report is still saved. Sign in with the email
            you use for Career Path DECIDE and we'll take you straight to it.
          </p>
        }
        @case ('claim') {
          <p>You'll use it to sign in from now on.</p>
        }
      }

      @if (view() === 'reg1' || view() === 'reg2') {
        <ol class="ac-steps">
          <li
            class="ac-step"
            [class.is-on]="view() === 'reg1'"
            [class.is-done]="view() === 'reg2'"
          >
            <b aria-hidden="true">1</b>You
          </li>
          <li class="ac-step-line" aria-hidden="true"></li>
          <li class="ac-step" [class.is-on]="view() === 'reg2'">
            <b aria-hidden="true">2</b>Your service
          </li>
        </ol>
      }
    </div>

    <div dialog-body>
      @switch (view()) {
        <!-- ------------------------------------------------------- SIGN IN -->
        @case ('signin') {
          <ng-container *ngTemplateOutlet="magicLinkForm"></ng-container>

          <!--
            ONE BAND, ONE CROSS-LINK. "Create one" is gone: the magic link is
            the create path, which is what the heading promises, and a member
            who has never signed in cannot tell which of two buttons is for
            them. The registration flow keeps its own /register entry.
          -->
          <p class="ac-band">
            Counselor or administrator?
            <button class="ac-link" type="button" (click)="go('counselor')">
              Sign in here
            </button>
          </p>
        }

        <!-- ---------------------------------------------------------- SENT -->
        @case ('sent') {
          <!-- A resend that failed. This view is reached only after one link
               has gone out, so without this the member presses "Send another
               link", nothing changes on screen, and they wait for an email
               that was never sent. -->
          @if (magicLinkError()) {
            <p class="ac-err" role="alert">{{ magicLinkError() }}</p>
          }

          <ul class="ac-sent-l">
            <li>The link expires in <b>15 minutes</b> and works once.</li>
            <li>
              It works in any browser or on any device, and every link we send
              stays good for 15 minutes.
            </li>
            <li>
              If it does not arrive, check spam before asking for another.
            </li>
          </ul>

          <cpd-flat-button
            type="button"
            color="primary"
            size="large"
            [text]="resendLabel()"
            [ngClass]="{ disabled: isResendBlocked() }"
            [disabled]="isResendBlocked()"
            (click)="resend()"
          >
          </cpd-flat-button>

          @if (lastSentAt()) {
            <p class="ac-note">
              Last sent at {{ lastSentAt() | date: 'shortTime' }}.
            </p>
          }

          <p class="ac-foot">
            <button class="ac-link" type="button" (click)="go('signin')">
              Use a different email
            </button>
          </p>
        }

        <!-- ------------------------------------------- REGISTER, STEP ONE -->
        @case ('reg1') {
          <form [formGroup]="identityFormGroup" (ngSubmit)="onIdentitySubmit()">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Full name</mat-label>
              <input
                matInput
                type="text"
                formControlName="name"
                autocomplete="name"
              />
              <mat-error>Please enter your name</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email</mat-label>
              <input
                matInput
                type="email"
                formControlName="email"
                autocomplete="email"
              />
              <mat-error>
                {{
                  identityFormGroup.controls.email.errors?.required
                    ? 'Please enter an email address'
                    : 'Please enter a valid email address'
                }}
              </mat-error>
            </mat-form-field>

            <div class="actions">
              <cpd-flat-button
                type="submit"
                color="primary"
                size="large"
                text="Continue"
              >
              </cpd-flat-button>
            </div>
          </form>

          <p class="ac-foot">
            Counselor or administrator?
            <button class="ac-link" type="button" (click)="go('counselor')">
              Sign in instead
            </button>
          </p>
        }

        <!-- ------------------------------------------- REGISTER, STEP TWO -->
        @case ('reg2') {
          <form [formGroup]="serviceFormGroup" (ngSubmit)="onServiceSubmit()">
            <!-- "Create my account" ends in a magic-link send, so a failure
                 has to be readable here too: this view does not render the
                 shared magic-link form that carries the same banner. -->
            @if (magicLinkError()) {
              <p class="ac-err" role="alert">{{ magicLinkError() }}</p>
            }

            <div class="ac-f">
              <span id="r2-branch-l" class="ac-f-label">Branch of service</span>
              @if (branches().length) {
                <cpd-branch-picker
                  [branches]="branches()"
                  [selectedBranchCode]="selectedBranch()?.branchCode"
                  labelledBy="r2-branch-l"
                  describedBy="r2-branch-e"
                  (branchSelected)="onBranchSelected($event)"
                >
                </cpd-branch-picker>
              } @else {
                <p class="ac-branches-empty" aria-live="polite">
                  {{
                    branchesFailed()
                      ? 'The branch list could not be loaded. You can still continue and add your branch later.'
                      : 'Loading the list of branches...'
                  }}
                </p>
              }
              <span class="ac-err" id="r2-branch-e" role="alert">
                @if (
                  serviceFormGroup.touched &&
                  !selectedBranch() &&
                  !branchesFailed()
                ) {
                  Please choose your branch of service
                }
              </span>
            </div>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Installation or nearest base</mat-label>
              <input
                matInput
                type="text"
                formControlName="installation"
                autocomplete="off"
                placeholder="Start typing"
                [matAutocomplete]="installations"
                (input)="onInstallationInput($any($event.target).value)"
              />
              <mat-error
                >Please enter your installation or nearest base</mat-error
              >
            </mat-form-field>

            <mat-autocomplete #installations="matAutocomplete">
              @for (match of installationMatches(); track match.id) {
                <mat-option
                  [value]="match.name"
                  (onSelectionChange)="onInstallationChosen(match)"
                >
                  {{ match.name }}
                </mat-option>
              }
            </mat-autocomplete>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>When you separate</mat-label>
              <input matInput type="month" formControlName="separationMonth" />
              <mat-hint
                >An estimate is fine, and you can leave this blank.</mat-hint
              >
            </mat-form-field>

            <div class="actions">
              <cpd-flat-button
                type="submit"
                color="primary"
                size="large"
                text="Create my account"
              >
              </cpd-flat-button>
              <button class="ac-link" type="button" (click)="go('reg1')">
                Back
              </button>
            </div>
          </form>
        }

        <!-- ----------------------------------------------------- COUNSELOR -->
        @case ('counselor') {
          <form
            #counselorForm="ngForm"
            (ngSubmit)="onCounselorSubmit(csEmail.value, csPassword.value)"
          >
            @if (counselorError()) {
              <p class="ac-err" role="alert">{{ counselorError() }}</p>
            }

            <label class="ac-field">
              <span>Work email</span>
              <span class="ac-input">
                <input
                  #csEmail
                  #csEmailModel="ngModel"
                  type="email"
                  name="username"
                  ngModel
                  email
                  autocomplete="username"
                  spellcheck="false"
                  placeholder="name@agency.gov"
                  required
                  [attr.aria-invalid]="
                    csEmailModel.touched && csEmailModel.invalid
                  "
                />
              </span>

              <!-- The per-field message the standalone page's mat-error gave,
                   and only once they have left the field: telling somebody
                   their address is invalid while they type it is not help. -->
              @if (csEmailModel.touched && csEmailModel.invalid) {
                <em class="ac-msg" role="alert">
                  Enter an email address, like name&#64;example.com
                </em>
              }
            </label>

            <!-- Forgot your password? sits on the Password label's row, right
                 aligned (Bethany, 5 Oct 2026). It comes after the field in
                 the source, so the tab order is password, reveal, then the
                 link; the grid lifts it onto the label's row. -->
            <div class="ac-pw">
              <label class="ac-field">
                <span>Password</span>
                <span class="ac-input">
                  <input
                    #csPassword
                    #csPasswordModel="ngModel"
                    [type]="isPasswordHidden() ? 'password' : 'text'"
                    name="password"
                    ngModel
                    autocomplete="current-password"
                    spellcheck="false"
                    required
                    [attr.aria-invalid]="
                      csPasswordModel.touched && csPasswordModel.invalid
                    "
                  />

                  <!-- Reveal. aria-pressed carries the state, so the control is
                       not read out identically in both of its two positions. -->
                  <button
                    class="ac-reveal"
                    type="button"
                    (click)="togglePasswordVisibility($event)"
                    [attr.aria-label]="
                      isPasswordHidden() ? 'Show password' : 'Hide password'
                    "
                    [attr.aria-pressed]="!isPasswordHidden()"
                  >
                    <fa-icon
                      [icon]="['fas', isPasswordHidden() ? 'eye-slash' : 'eye']"
                    ></fa-icon>
                  </button>
                </span>

                @if (csPasswordModel.touched && csPasswordModel.invalid) {
                  <em class="ac-msg" role="alert">
                    Enter your password
                  </em>
                }
              </label>

              <button
                class="ac-link ac-forgot"
                type="button"
                (click)="forgotPassword()"
              >
                Forgot your password?
              </button>
            </div>

            <div class="actions">
              <cpd-flat-button
                type="submit"
                color="primary"
                size="large"
                text="Sign in"
                [ngClass]="{ disabled: !counselorForm.form.valid }"
                [disabled]="!counselorForm.form.valid"
              >
              </cpd-flat-button>
            </div>
          </form>

          <!-- The same band the learner card wears, carrying both ways out:
               one row on desktop, stacked on a phone. Request access is
               ANALYTICS.2 / PWS 4.4.1.2.2, carried over from the standalone
               page this view replaced; see the field's own note for why it
               is not the voided access-request mutation. -->
          <div class="ac-band ac-band--split">
            <p>
              Not a counselor?
              <button class="ac-link" type="button" (click)="go('signin')">
                Sign in as a learner
              </button>
            </p>
            @if (dantesReportingEnabled$ | async) {
              <p>
                No account yet?
                <a
                  class="ac-link"
                  routerLink="/account/request-access"
                  (click)="close()"
                >
                  Request access
                </a>
              </p>
            }
          </div>
        }

        <!-- ----------------------------------------------------- CONTINUE -->
        <!-- AUTH.7. The page opened from an emailed link. The link is spent
             only on this press: mail scanners open links to check them, and
             spending on page load let the scanner sign in first. -->
        @case ('continue') {
          @if (magicLinkError()) {
            <p class="ac-err" role="alert">{{ magicLinkError() }}</p>
          }

          <cpd-flat-button
            type="button"
            color="primary"
            size="large"
            [text]="isContinuing() ? 'Signing in…' : 'Continue to sign in'"
            [ngClass]="{ disabled: isContinuing() }"
            [disabled]="isContinuing()"
            (click)="continueSignIn()"
          >
          </cpd-flat-button>

          <p class="ac-note">
            This extra step keeps email security scanners from using your link
            before you can.
          </p>

          <p class="ac-foot">
            <button class="ac-link" type="button" (click)="go('signin')">
              Not you? Use a different email
            </button>
          </p>
        }

        <!-- --------------------------------------------------------- USED -->
        <!-- AUTH.7. Replaces a one-second toast and a reload to the home
             page, which left the member with no idea what happened. -->
        @case ('used') {
          @if (magicLinkError()) {
            <p class="ac-err" role="alert">{{ magicLinkError() }}</p>
          }

          <ul class="ac-sent-l">
            <li>If you have more than one email from us, try the newest one.</li>
            <li>Or ask for a new link below. It can take a few minutes to arrive.</li>
          </ul>

          <cpd-flat-button
            type="button"
            color="primary"
            size="large"
            text="Email me a new link"
            (click)="requestNewLink()"
          >
          </cpd-flat-button>

          <p class="ac-note">
            We will send it to <b>{{ landedEmail() }}</b>.
          </p>

          <p class="ac-foot">
            <button class="ac-link" type="button" (click)="go('signin')">
              Sign in another way
            </button>
          </p>
        }

        <!-- ------------------------------------------------ RETURN EXPIRED -->
        <!-- AUTH.9. An old goals report link that has expired, for a member
             who has an email, or a link we do not recognise. The same form
             as the sign-in card; signing in lands on the Goals Report. -->
        @case ('returnExpired') {
          <ng-container *ngTemplateOutlet="magicLinkForm"></ng-container>

          <p class="ac-band">
            Never added an email? Open the link again from your original email
            and we'll renew it for you.
          </p>
        }

        <!-- --------------------------------------------------------- CLAIM -->
        <!-- AUTH.9. From the banner on an old link's report, for a member with
             no email. Once signed in, the email is attached to that report. -->
        @case ('claim') {
          <ng-container *ngTemplateOutlet="magicLinkForm"></ng-container>
        }

        <!-- ---------------------------------------------------------- DONE -->
        @case ('done') {
          <div class="ac-done">
            <p>Your account is ready.</p>
            <cpd-flat-button
              type="button"
              color="primary"
              size="large"
              text="Close"
              (click)="close()"
            >
            </cpd-flat-button>
          </div>
        }
      }
    </div>
  </cpd-dialog-container2>
}

<!-- One polite region for the whole dialog. Every view change and every send
     lands here, because none of them has a visual anchor a screen reader would
     otherwise reach. -->
<div aria-live="polite" class="cdk-visually-hidden">{{ announcement() }}</div>
